import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Select,
  MenuItem,
  Backdrop,
  FormControl,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  DialogContentText,
} from "@mui/material";
import AppLoader from '../../../components/AppLoader/AppLoader';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useSelector } from "react-redux";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CloseIcon from "@mui/icons-material/Close";
import CheckPermissions from "../../../authorization/authorization";
import { permissionsVariables } from "../../../services/Functions";
import { cfpBaseUrl } from "../../../network/urls";
import "./EopsUpdates.scss";
// disco logos
import PITCDDLOGO from "../../../images/discologos/pitc11-removebg-preview.png";
import LESCODDLOGO from "../../../images/discologos/lesco.jpg";
import GEPCODDLOGO from "../../../images/discologos/gepco.jpg";
import FESCODDLOGO from "../../../images/discologos/fesco.jpg";
import IESCODDLOGO from "../../../images/discologos/iesco logo.png";
import MEPCODDLOGO from "../../../images/discologos/mepco1.jpg";
import PESCODDLOGO from "../../../images/discologos/pesco1.jpg";
import HAZECODDLOGO from "../../../images/discologos/hazeco_logo-removebg-preview.png";
import SEPCODDLOGO from "../../../images/discologos/sepco.jpg";
import HESCODDLOGO from "../../../images/discologos/hesco logo.png";
import QESCODDLOGO from "../../../images/discologos/qesco.png";
import TESCODDLOGO from "../../../images/discologos/tesco.jpg";
import "./EopsUpdates.scss";

// Seed data removed — dashboard uses API-provided eOps updates only
// ─── Design tokens ────────────────────────────────────────────────────────────
const tk = {
  blue800: "#0C447C",
  blue600: "#185FA5",
  blue400: "#378ADD",
  blue200: "#85B7EB",
  blue100: "#B5D4F4",
  blue50: "#E6F1FB",
  green600: "#3B6D11",
  greenVivid: "#22C55E",
  greenVividSoft: "#DCFCE7",
  greenVividBorder: "#86EFAC",
  red600: "#A32D2D",
  red100: "#F7C1C1",
  red50: "#FCEBEB",
  border: "rgba(0,0,0,0.10)",
  borderHover: "rgba(0,0,0,0.18)",
  surface: "#ffffff",
  surfaceSub: "#F6F9FD",
  textPri: "#0F172A",
  textSec: "#475569",
  textHint: "#94A3B8",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function createRowId(prefix = "upd") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// seedWithIds removed; component will fetch rows from API

function relDate(d) {
  const diff = Math.floor((new Date() - new Date(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "1d ago";
  return `${diff}d ago`;
}

function fullDate(d) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function todayStr() {
  const t = new Date();
  return new Date(t - t.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
}

function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function validate(v) {
  const e = {};
  if (!v.updateTypeId) e.updateTypeId = "Select update type.";
  if (!v.officeCode || (Array.isArray(v.officeCode) && v.officeCode.length === 0)) e.officeCode = "Select company.";
  if (!v.title.trim()) e.title = "Title is required.";
  else if (v.title.trim().length < 3) e.title = "Min 3 characters.";
  if (!v.description.trim()) e.description = "Description is required.";
  else if (v.description.trim().length < 10)
    e.description = "Min 10 characters.";
  if (!v.date) e.date = "Date is required.";
  else if (v.date > todayStr()) e.date = "Future date not allowed.";
  return e;
}

const EMPTY_FORM = { title: "", description: "", date: "", updateTypeId: "", isActive: true, officeCode: [] };

// Company options mapping
const COMPANY_OPTIONS = [
  { code: "10", name: "Power Information Technology Company" },
  { code: "11", name: "Lahore Electric Supply Company" },
  { code: "12", name: "Gujranwala Electric Power Company" },
  { code: "13", name: "Faisalabad Electric Supply Company" },
  { code: "14", name: "Islamabad Electric Supply Company" },
  { code: "15", name: "Multan Electric Power Company" },
  { code: "26", name: "Peshawar Electric Supply Company" },
  { code: "27", name: "Hazara Electric Supply Company" },
  { code: "38", name: "Sukkur Electric Power Company" },
  { code: "37", name: "Hyderabad Electric Supply Company" },
  { code: "48", name: "Quetta Electric Supply Company" },
  { code: "59", name: "Tribal Electric Supply Company" },
];

function getCompanyLogo(code) {
  switch (String(code)) {
    case "10":
      return PITCDDLOGO;
    case "11":
      return LESCODDLOGO;
    case "12":
      return GEPCODDLOGO;
    case "13":
      return FESCODDLOGO;
    case "14":
      return IESCODDLOGO;
    case "15":
      return MEPCODDLOGO;
    case "26":
      return PESCODDLOGO;
    case "27":
      return HAZECODDLOGO;
    case "37":
      return HESCODDLOGO;
    case "38":
      return SEPCODDLOGO;
    case "48":
      return QESCODDLOGO;
    case "59":
      return TESCODDLOGO;
    default:
      return PITCDDLOGO;
  }
}

function getUpdateTheme(typeName = "") {
  const t = String(typeName).toLowerCase();
  if (t.includes("api") || t.includes("integration")) return "blue";
  if (t.includes("release")) return "purple";
  if (t.includes("developer") || t.includes("dev")) return "orange";
  if (t.includes("security") || t.includes("shield")) return "teal";
  if (t.includes("system")) return "green";
  const themes = ["blue", "green", "purple", "orange", "teal"];
  let hash = 0;
  for (let i = 0; i < t.length; i += 1) hash += t.charCodeAt(i);
  return themes[hash % themes.length] || "blue";
}

function getUpdateTypeIcon(theme) {
  switch (theme) {
    case "green":
      return DescriptionOutlinedIcon;
    case "purple":
      return RocketLaunchOutlinedIcon;
    case "orange":
      return CodeOutlinedIcon;
    case "teal":
      return ShieldOutlinedIcon;
    default:
      return CampaignOutlinedIcon;
  }
}

const STAT_CHART = {
  total: ["42%", "68%", "35%", "80%", "55%", "72%"],
  week: ["30%", "55%", "40%", "75%", "48%", "62%"],
  earlier: ["50%", "35%", "65%", "40%", "70%", "45%"],
};

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ value, title, hint, variant = "total", icon: Icon }) {
  return (
    <div className={`eops-stat eops-stat--${variant}`}>
      <div className="eops-stat__main">
        {Icon && (
          <span className="eops-stat__icon" aria-hidden="true">
            <Icon />
          </span>
        )}
        <div className="eops-stat__copy">
          <p className="eops-stat__title">
            <strong>{value}</strong> {title}
          </p>
          <p className="eops-stat__hint">{hint}</p>
        </div>
      </div>
      <div className="eops-stat__chart" aria-hidden="true">
        {(STAT_CHART[variant] || STAT_CHART.total).map((h, i) => (
          <span key={`${variant}-${i}`} style={{ "--h": h }} />
        ))}
      </div>
    </div>
  );
}

// ─── FilterChip (legacy name kept for clarity in list filters) ────────────────
function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      className={`eops-toolbar__filter${active ? " eops-toolbar__filter--active" : ""}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// ─── ActionBtn ────────────────────────────────────────────────────────────────
function ActionBtn({ onClick, danger, title, children }) {
  return (
    <IconButton
      title={title}
      size="small"
      onClick={onClick}
      className={`eops-action-btn${danger ? " eops-action-btn--danger" : ""}`}
    >
      {children}
    </IconButton>
  );
}

// ─── MetaRow ──────────────────────────────────────────────────────────────────
function MetaRow({ date, author }) {
  return (
    <div className="eops-update__meta">
      <span className="eops-update__meta-item">
        <CalendarTodayOutlinedIcon />
        {fullDate(date)}
      </span>
      <span className="eops-update__meta-sep" aria-hidden="true">
        |
      </span>
      <span className="eops-update__meta-item">
        <PersonOutlineIcon />
        {author}
      </span>
    </div>
  );
}

// ─── UpdateCard ───────────────────────────────────────────────────────────────
function UpdateCard({ row, onView, onEdit, permissions }) {
  const theme = getUpdateTheme(row.updateTypeName);
  const TypeIcon = getUpdateTypeIcon(theme);

  return (
    <div className={`eops-update eops-update--${theme}`}>
      <span className="eops-update__dot" aria-hidden="true" />

      <div className="eops-update__card">
        <span className="eops-update__type-icon" aria-hidden="true">
          <TypeIcon />
        </span>

        <div className="eops-update__body">
          <div className="eops-update__main">
            <div className="eops-update__title-row">
              <h4 className="eops-update__title">{row.title}</h4>
              {row.updateTypeName && (
                <span className="eops-update__type">{row.updateTypeName}</span>
              )}
            </div>
            {row.description ? (
              <p className="eops-update__desc">{row.description}</p>
            ) : null}
            <MetaRow date={row.date} author={row.author} />
          </div>

          <div className="eops-update__aside">
            <span className="eops-update__rel">{relDate(row.date)}</span>
            <span
              className={`eops-update__status ${
                row.isActive
                  ? "eops-update__status--active"
                  : "eops-update__status--inactive"
              }`}
            >
              <span className="eops-update__status-dot" aria-hidden="true" />
              {row.isActive ? "Active" : "Inactive"}
            </span>
            <div className="eops-update__actions">
              <ActionBtn title="View" onClick={() => onView(row)}>
                <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
              </ActionBtn>
              {CheckPermissions(
                permissions,
                permissionsVariables.accessManagement,
                "eOps Updates",
                "Update",
              ) && (
                <ActionBtn title="Edit" onClick={() => onEdit(row)}>
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </ActionBtn>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TimelineSection ──────────────────────────────────────────────────────────
function TimelineSection({
  label,
  variant = "week",
  rows: sectionRows,
  ...cardProps
}) {
  if (!sectionRows.length) return null;
  const SectionIcon =
    variant === "earlier" ? HistoryOutlinedIcon : CalendarTodayOutlinedIcon;

  return (
    <section className={`eops-section eops-section--${variant}`}>
      <div className="eops-section__head">
        <span className="eops-section__icon" aria-hidden="true">
          <SectionIcon />
        </span>
        <h3 className="eops-section__title">{label}</h3>
        <span className="eops-section__count">
          {sectionRows.length} update{sectionRows.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="eops-timeline">
        {sectionRows.map((row) => (
          <UpdateCard
            key={`${row.id}-${row.title}-${row.date}`}
            row={row}
            {...cardProps}
          />
        ))}
      </div>
    </section>
  );
}

// ─── FormFields ───────────────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 13,
    "& fieldset": { borderColor: tk.border, borderWidth: "0.5px" },
    "&:hover fieldset": { borderColor: tk.blue200 },
    "&.Mui-focused fieldset": { borderColor: tk.blue400, borderWidth: "1px" },
  },
};
const fLabelSx = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: tk.textSec,
  mb: 0.75,
};

function FormFields({ values, errors, onChange, updateTypes = [], showStatus = false }) {
  return (
    <>
          <Box sx={{ mt: 2 }}>
            <Typography className="eops-updates-form-label" sx={fLabelSx}>Company</Typography>
            <FormControl fullWidth>
              <Select
                multiple
                size="small"
                value={values.officeCode || []}
                onChange={(e) => onChange("officeCode", e.target.value)}
                displayEmpty
                sx={inputSx}
                MenuProps={{
                  PaperProps: { className: "eops-updates-select-menu", style: { maxHeight: 320 } },
                }}
                renderValue={(selected) => {
                  if (!selected || (Array.isArray(selected) && selected.length === 0)) return "Select company";
                  const arr = Array.isArray(selected) ? selected : [selected];
                  const names = arr
                    .map((c) => COMPANY_OPTIONS.find((co) => co.code === String(c))?.name || c)
                    .filter(Boolean);
                  return names.join(", ");
                }}
              >
                {COMPANY_OPTIONS.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <Checkbox checked={(values.officeCode || []).indexOf(String(c.code)) > -1} size="small" />
                      <Typography sx={{ fontSize: 13, flex: 1 }}>{c.name}</Typography>
                      <img
                        src={getCompanyLogo(c.code)}
                        alt={`${c.name} logo`}
                        style={{ width: 30, height: 30, marginLeft: 12 }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = PITCDDLOGO;
                        }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.officeCode && (
              <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
                {errors.officeCode}
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography className="eops-updates-form-label" sx={fLabelSx}>Type</Typography>
            <FormControl fullWidth>
              <Select
                size="small"
                value={values.updateTypeId || ""}
                onChange={(e) => onChange("updateTypeId", e.target.value)}
                displayEmpty
                sx={inputSx}
                MenuProps={{
                  PaperProps: { className: "eops-updates-select-menu" },
                }}
              >
                <MenuItem value="">Select type</MenuItem>
                {updateTypes.map((t) => (
                  <MenuItem key={t.updateTypeId} value={String(t.updateTypeId)}>
                    {t.updateTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.updateTypeId && (
              <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
                {errors.updateTypeId}
              </Typography>
            )}
          </Box>
      <Box sx={{ mt: 2 }}>
        <Typography className="eops-updates-form-label" sx={fLabelSx}>Title</Typography>
        <TextField
          fullWidth
          size="small"
          sx={inputSx}
          value={values.title}
          placeholder="Enter update title"
          onChange={(e) => onChange("title", e.target.value)}
        />
        {errors.title && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.title}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography className="eops-updates-form-label" sx={fLabelSx}>Description</Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          size="small"
          sx={inputSx}
          value={values.description}
          placeholder="Enter description"
          onChange={(e) => onChange("description", e.target.value)}
        />
        {errors.description && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.description}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography className="eops-updates-form-label" sx={fLabelSx}>Date</Typography>
        <TextField
          type="date"
          fullWidth
          size="small"
          sx={inputSx}
          value={values.date}
          inputProps={{ max: todayStr() }}
          onChange={(e) => onChange("date", e.target.value)}
        />
        {errors.date && (
          <Typography sx={{ fontSize: 11, color: tk.red600, mt: 0.5 }}>
            {errors.date}
          </Typography>
        )}
      </Box>
      {showStatus && (
        <Box sx={{ mt: 2 }}>
          <Typography className="eops-updates-form-label" sx={fLabelSx}>Status</Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              name="isActive"
              value={values.isActive ? String(1) : String(0)}
              onChange={(e) => onChange("isActive", e.target.value === String(1))}
            >
              <FormControlLabel
                value={String(1)}
                control={<Radio size="small" />}
                label="Active"
              />
              <FormControlLabel
                value={String(0)}
                control={<Radio size="small" />}
                label="Inactive"
              />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
      
    </>
  );
}

// ─── ModalShell ───────────────────────────────────────────────────────────────
const cancelBtnSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontSize: 13,
  border: `0.5px solid ${tk.border}`,
  color: tk.textSec,
  "&:hover": { background: tk.surfaceSub },
};
const primaryBtnSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontSize: 13,
  fontWeight: 600,
  background: "#006ad1",
  color: "#fff",
  boxShadow: "0 2px 8px rgba(0, 106, 209, 0.28)",
  "&:hover": { background: "#0057ad", boxShadow: "0 4px 12px rgba(0, 106, 209, 0.34)" },
};

function ModalShell({ open, onClose, title, children, footer }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "eops-updates-modal", sx: { borderRadius: "14px", overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          background: "#006ad1",
          color: "#fff",
          fontSize: 14,
          fontWeight: 600,
          py: 1.75,
          pr: 6,
        }}
      >
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: "#fff",
            "&:hover": { background: "rgba(255,255,255,0.15)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>{children}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>{footer}</DialogActions>
    </Dialog>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const EopsUpdates = () => {
  const [rows, setRows] = useState([]);
  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [formVals, setFormVals] = useState(EMPTY_FORM);
  const [formErrs, setFormErrs] = useState({});
  const [editVals, setEditVals] = useState(EMPTY_FORM);
  const [editErrs, setEditErrs] = useState({});
  const { permissions, token, code } = useSelector((s) => s.auth);
  const [updateTypes, setUpdateTypes] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success", refreshOnClose: false });

  const sorted = useMemo(
    () => [...rows].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [rows],
  );

  React.useEffect(() => {
    const fetchTypes = async () => {
      try {
        const myHeaders = new Headers();
        if (token) myHeaders.append("Authorization", token);
        const requestOptions = { method: "GET", headers: myHeaders, redirect: "follow" };
        const res = await fetch(cfpBaseUrl + "eOpsUpdates/types", requestOptions);
        const text = await res.text();
        let json = null;
        try {
          json = JSON.parse(text);
        } catch (e) {
          // fallback if API already returns JSON
          json = res.ok ? await res.json() : null;
        }
        if (json && json.data) setUpdateTypes(json.data);
      } catch (e) {
      }
    };
    fetchTypes();
  }, [token]);

  React.useEffect(() => {
    // fetchUpdates declared below and reused after edits
    fetchUpdates();
  }, [token, code, updateTypes]);

  // Reusable fetch function for updates list
  async function fetchUpdates() {
    try {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      if (token) myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");

      const body = JSON.stringify({ pageNumber: "1", pageSize: "20", officeCode: code ? String(code).slice(0, 2) : "10" });
      const requestOptions = { method: "POST", headers: myHeaders, body, redirect: "follow" };

      const res = await fetch(cfpBaseUrl + "eOpsUpdates/get", requestOptions);
      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch (e) {
        json = res.ok ? await res.json() : null;
      }

      if (json && (json.status === "200" || json.status === 200) && Array.isArray(json.data)) {
        const mapped = json.data.map((it) => ({
          id: `api-${it.id}`,
          title: it.title,
          description: it.description,
          date: it.releaseDate ? it.releaseDate.split("T")[0] : it.releaseDate,
          officeCode: it.companyCode ? String(it.companyCode) : "",
          author: it.createdBy || "",
          updateTypeId: it.updateTypeId,
          updateTypeName: updateTypes.find((t) => Number(t.updateTypeId) === Number(it.updateTypeId))?.updateTypeName || "",
          isActive: (it.isActive === 1 || it.isActive === "1" || it.isActive === true || it.isActive === "true") ? true : false,
        }));
        setRows(mapped);
      } else {
        setRows([]);
      }
    } catch (e) {
      setRows([]);
    } finally {
      setViewBackdrop(false);
    }
  }

  const displayed = useMemo(() => {
    let list = sorted;
    if (search) {
      const q = search.trim();
      const pattern = new RegExp(escapeRegex(q), "i");
      list = list.filter(
        (r) =>
          pattern.test(String(r.title || "")) ||
          pattern.test(String(r.description || "")),
      );
    }
    if (filter === "week")
      list = list.filter(
        (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
      );
    if (filter === "earlier")
      list = list.filter(
        (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
      );
    return list;
  }, [sorted, search, filter]);

  const weekRows = displayed.filter(
    (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
  );
  const earlierRows = displayed.filter(
    (r) => Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
  );

  const handleAddClose = () => {
    setAddOpen(false);
    setFormVals(EMPTY_FORM);
    setFormErrs({});
  };
  const handleAddSubmit = () => {
    (async () => {
      const errs = validate(formVals);
      setFormErrs(errs);
      if (Object.keys(errs).length) return;

      setViewBackdrop(true);

      try {
        const myHeaders = new Headers();
        if (token) myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const now = new Date();
        const timePart = now.toTimeString().split(" ")[0]; // HH:MM:SS
        const datePart = formVals.date ? formVals.date : now.toISOString().split("T")[0];
        const releaseDateWithTime = `${datePart} ${timePart}`; // YYYY-MM-DD HH:MM:SS

        const payload = {
          title: formVals.title.trim(),
          description: formVals.description.trim(),
          releaseDate: releaseDateWithTime,
          updateTypeId: parseInt(formVals.updateTypeId, 10) || 0,
          isActive: 1,
          officeCode: Array.isArray(formVals.officeCode) && formVals.officeCode.length
            ? formVals.officeCode.join(",")
            : formVals.officeCode || (code ? String(code).slice(0, 2) : "11"),
        };

        const requestOptions = {
          method: "POST",
          headers: myHeaders,
          body: JSON.stringify(payload),
          redirect: "follow",
        };

        const res = await fetch(cfpBaseUrl + "eOpsUpdates/add", requestOptions);
        const text = await res.text();
        let result = null;
        try {
          result = JSON.parse(text);
        } catch (e) {
          result = res.ok ? await res.json() : null;
        }

        if (result && (result.status === "200" || result.status === 200)) {
          const typeObj = updateTypes.find(
            (t) => String(t.updateTypeId) === String(formVals.updateTypeId),
          );
            setRows((p) => [
            {
              id: createRowId(),
              title: formVals.title.trim(),
              description: formVals.description.trim(),
                date: releaseDateWithTime.split(" ")[0],
                author: "Admin User",
                officeCode: Array.isArray(formVals.officeCode) && formVals.officeCode.length
                  ? formVals.officeCode.join(",")
                  : formVals.officeCode || (code ? String(code).slice(0, 2) : "11"),
              updateTypeId: formVals.updateTypeId,
              updateTypeName: typeObj ? typeObj.updateTypeName : "",
              isActive: true,
            },
            ...p,
          ]);
          setSnackbar({ open: true, message: result.message || "Added successfully", severity: "success", refreshOnClose: false });
          setFilter("all");
          setSearch("");
          handleAddClose();
        } else {
          setSnackbar({ open: true, message: (result && result.message) || "Something went wrong", severity: "error" });
        }
      } catch (e) {
        setSnackbar({ open: true, message: "Network or server error", severity: "error" });
      } finally {
        setViewBackdrop(false);
      }
    })();
  };

  const handleEditOpen = (row) => {
    setEditRow(row);
    setEditVals({
      title: row.title,
      description: row.description,
      officeCode: row.officeCode ? String(row.officeCode).split(",") : [],
      date: row.date,
      updateTypeId: row.updateTypeId || "",
      isActive: (row.isActive === 1 || row.isActive === "1" || row.isActive === true || row.isActive === "true") ? true : false,
    });
    setEditErrs({});
  };
  const handleEditClose = () => {
    setEditRow(null);
    setEditVals(EMPTY_FORM);
    setEditErrs({});
  };
  const handleEditSubmit = () => {
    (async () => {
      const errs = validate(editVals);
      setEditErrs(errs);
      if (Object.keys(errs).length || !editRow) return;

      setViewBackdrop(true);

      try {
        // If this row came from API it has id like `api-<num>`
        const serverId = editRow && String(editRow.id).startsWith("api-")
          ? String(editRow.id).replace("api-", "")
          : null;

        if (serverId) {
          const myHeaders = new Headers();
          if (token) myHeaders.append("Authorization", token);
          myHeaders.append("Content-Type", "application/json");

          const now = new Date();
          const timePart = now.toTimeString().split(" ")[0];
          const datePart = editVals.date ? editVals.date : now.toISOString().split("T")[0];
          const releaseDateWithTime = `${datePart} ${timePart}`; // YYYY-MM-DD HH:MM:SS

          const payload = {
            id: Number(serverId),
            title: editVals.title.trim(),
            description: editVals.description.trim(),
            releaseDate: releaseDateWithTime,
            updateTypeId: parseInt(editVals.updateTypeId, 10) || 0,
            isActive: Boolean(editVals.isActive),
            officeCode: Array.isArray(editVals.officeCode) && editVals.officeCode.length
              ? editVals.officeCode.join(",")
              : editVals.officeCode || (code ? String(code).slice(0, 2) : "11"),
          };

          const requestOptions = {
            method: "PATCH",
            headers: myHeaders,
            body: JSON.stringify(payload),
            redirect: "follow",
          };

          const res = await fetch(cfpBaseUrl + "eOpsUpdates/update", requestOptions);
          const text = await res.text();
          let result = null;
          try {
            result = JSON.parse(text);
          } catch (e) {
            result = res.ok ? await res.json() : null;
          }

          if (result && (result.status === "200" || result.status === 200)) {
            // Update local row with edited values
            setRows((p) =>
              p.map((r) =>
                r.id === editRow.id
                  ? {
                      ...r,
                      title: editVals.title.trim(),
                      description: editVals.description.trim(),
                      date: releaseDateWithTime.split(" ")[0],
                      author: r.author || "Admin User",
                      officeCode: Array.isArray(editVals.officeCode) && editVals.officeCode.length
                        ? editVals.officeCode.join(",")
                        : editVals.officeCode || (code ? String(code).slice(0, 2) : "11"),
                      updateTypeId: editVals.updateTypeId || r.updateTypeId,
                      updateTypeName:
                        updateTypes.find((t) => String(t.updateTypeId) === String(editVals.updateTypeId))
                          ?.updateTypeName || r.updateTypeName,
                      isActive: editVals.isActive === undefined ? r.isActive : Boolean(editVals.isActive),
                    }
                  : r,
              ),
            );

            setSnackbar({ open: true, message: result.message || "Updated successfully", severity: "success", refreshOnClose: true });
            handleEditClose();
          } else {
            setSnackbar({ open: true, message: (result && result.message) || "Something went wrong", severity: "error" });
            }
        } else {
          // Local-only row (seed/local). Update locally and show success.
          setRows((p) =>
            p.map((r) =>
              r.id === editRow.id
                  ? {
                    ...r,
                    title: editVals.title.trim(),
                    description: editVals.description.trim(),
                    date: editVals.date,
                    author: r.author || "Admin User",
                    updateTypeId: editVals.updateTypeId || r.updateTypeId,
                    updateTypeName:
                      updateTypes.find((t) => String(t.updateTypeId) === String(editVals.updateTypeId))
                        ?.updateTypeName || r.updateTypeName,
                    isActive: editVals.isActive === undefined ? r.isActive : Boolean(editVals.isActive),
                  }
                : r,
            ),
          );
          setSnackbar({ open: true, message: "Updated locally", severity: "success", refreshOnClose: false });
          handleEditClose();
        }
        } catch (e) {
          setSnackbar({ open: true, message: "Network or server error", severity: "error" });
        } finally {
          setViewBackdrop(false);
        }
    })();
  };

  const handleDelete = (id) => setRows((p) => p.filter((r) => r.id !== id));

  const cardProps = {
    onView: setViewRow,
    onEdit: handleEditOpen,
    onDelete: handleDelete,
    permissions,
  };

  return (
    <Box className="form-box eops-updates-page">
      <Box className="application-table eops-updates-page__content">
        {/* Header */}
        <div className="eops-header">
          <div className="eops-header__left">
            <h2 className="eops-header__title">eOps updates</h2>
            <span className="eops-header__count">{rows.length} updates</span>
          </div>
          {CheckPermissions(
            permissions,
            permissionsVariables.accessManagement,
            "eOps Updates",
            "Create",
          ) && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              onClick={() => setAddOpen(true)}
              className="eops-header__add"
              sx={primaryBtnSx}
            >
              Add update
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="eops-stats">
          <StatCard
            value={rows.length}
            title="Total Updates"
            hint="All time updates"
            variant="total"
            icon={CampaignOutlinedIcon}
          />
          <StatCard
            value={
              rows.filter(
                (r) =>
                  Math.floor((new Date() - new Date(r.date)) / 86400000) <= 7,
              ).length
            }
            title="This Week"
            hint="Updates this week"
            variant="week"
            icon={CalendarTodayOutlinedIcon}
          />
          <StatCard
            value={
              rows.filter(
                (r) =>
                  Math.floor((new Date() - new Date(r.date)) / 86400000) > 7,
              ).length
            }
            title="Earlier"
            hint="Older updates"
            variant="earlier"
            icon={HistoryOutlinedIcon}
          />
        </div>

        {/* Search + filters — dedicated toolbar */}
        <div className="eops-toolbar">
          <div className="eops-toolbar__search">
            <span className="eops-toolbar__search-badge" aria-hidden="true">
              <SearchOutlinedIcon className="eops-toolbar__search-icon" />
            </span>
            <input
              type="text"
              className="eops-toolbar__search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search updates by title, type, author…"
              aria-label="Search updates"
            />
          </div>
          <div className="eops-toolbar__divider" aria-hidden="true" />
          <div className="eops-toolbar__filters" role="group" aria-label="Filter by period">
            {[
              ["all", "All"],
              ["week", "This week"],
              ["earlier", "Earlier"],
            ].map(([val, lbl]) => (
              <FilterChip
                key={val}
                label={lbl}
                active={filter === val}
                onClick={() => setFilter(val)}
              />
            ))}
          </div>
        </div>

        {/* List */}
        {displayed.length === 0 ? (
          <div className="eops-empty">No updates match your search</div>
        ) : (
          <>
            {filter !== "earlier" && (
              <TimelineSection
                label="This Week"
                variant="week"
                rows={weekRows}
                {...cardProps}
              />
            )}
            {filter !== "week" && (
              <TimelineSection
                label="Earlier"
                variant="earlier"
                rows={earlierRows}
                {...cardProps}
              />
            )}
          </>
        )}

        {/* Add dialog */}
        <ModalShell
          open={addOpen}
          onClose={handleAddClose}
          title="Add eOps update"
          footer={
            <>
              <Button onClick={handleAddClose} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddSubmit}
                sx={primaryBtnSx}
              >
                Submit
              </Button>
            </>
          }
        >
          <FormFields
              values={formVals}
              errors={formErrs}
              updateTypes={updateTypes}
              onChange={(f, v) => {
                setFormVals((p) => ({ ...p, [f]: v }));
                setFormErrs((p) => ({ ...p, [f]: "" }));
              }}
              showStatus={false}
            />
        </ModalShell>

        {/* View dialog */}
        <ModalShell
          open={Boolean(viewRow)}
          onClose={() => setViewRow(null)}
          title="View update"
          footer={
            <Button
              variant="contained"
              onClick={() => setViewRow(null)}
              sx={primaryBtnSx}
            >
              Close
            </Button>
          }
        >
          {viewRow && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              <Box>
                <Typography sx={{ ...fLabelSx, color: tk.textHint }} className="eops-updates-form-label">
                  Title
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography className="eops-updates-view-title" sx={{ fontSize: 15, fontWeight: 600, color: tk.blue600 }}>
                    {viewRow.title}
                  </Typography>
                  {viewRow.updateTypeName && (
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: tk.blue600, bgcolor: tk.greenVividSoft, px: 1, py: 0.25, borderRadius: 1 }}>
                      {viewRow.updateTypeName}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ ...fLabelSx, color: tk.textHint }}>
                  Description
                </Typography>
                <Typography
                  className="eops-updates-view-desc"
                  sx={{ fontSize: 13, color: tk.textSec, lineHeight: 1.65 }}
                >
                  {viewRow.description}
                </Typography>
              </Box>
              <Box className="eops-updates-view-divider" sx={{ borderTop: `0.5px solid ${tk.border}`, pt: 1.5 }}>
                <MetaRow date={viewRow.date} author={viewRow.author} />
              </Box>
            </Box>
          )}
        </ModalShell>

        {/* Edit dialog */}
        <ModalShell
          open={Boolean(editRow)}
          onClose={handleEditClose}
          title="Edit update"
          footer={
            <>
              <Button onClick={handleEditClose} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleEditSubmit}
                sx={primaryBtnSx}
              >
                Save changes
              </Button>
            </>
          }
        >
          <FormFields
            values={editVals}
            errors={editErrs}
            updateTypes={updateTypes}
            onChange={(f, v) => {
              setEditVals((p) => ({ ...p, [f]: v }));
              setEditErrs((p) => ({ ...p, [f]: "" }));
            }}
            showStatus={true}
          />
          {/* show selected type name above title for edit */}
          {editVals && editVals.updateTypeId && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ ...fLabelSx, color: tk.textHint }}>Selected Type</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: tk.blue600 }}>
                {updateTypes.find((t) => String(t.updateTypeId) === String(editVals.updateTypeId))?.updateTypeName || "-"}
              </Typography>
            </Box>
          )}
        </ModalShell>
      </Box>
      <Dialog
        open={snackbar.open}
        onClose={async () => {
          try {
            if (snackbar.refreshOnClose) await fetchUpdates();
          } catch (e) {
            /* ignore */
          }
          setSnackbar((p) => ({ ...p, open: false, refreshOnClose: false }));
        }}
        className={`dde-result-dialog${
          snackbar.severity === "error" ? " dde-result-dialog--reject" : ""
        }`}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: "dde-result-dialog__paper" }}
      >
        <DialogTitle className="dde-result-dialog__title">
          <Box className="dde-result-dialog__icon-wrap">
            {snackbar.severity === "error" ? (
              <HighlightOffIcon className="dde-result-dialog__icon dde-result-dialog__icon--reject" />
            ) : (
              <CheckCircleIcon className="dde-result-dialog__icon dde-result-dialog__icon--approve" />
            )}
          </Box>
          <Typography variant="h6" className="dde-result-dialog__heading">
            {snackbar.severity === "error"
              ? "Request Failed"
              : String(snackbar.message).toLowerCase().includes("update")
                ? "Updated Successfully"
                : "Added Successfully"}
          </Typography>
        </DialogTitle>
        <DialogContent className="dde-result-dialog__content">
          <DialogContentText component="div">
            <p className="dde-result-dialog__message">{snackbar.message}</p>
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Backdrop open={viewBackdrop} sx={{ zIndex: 1400, color: '#fff' }}>
        <AppLoader />
      </Backdrop>
    </Box>
  );
};

export default EopsUpdates;
